import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Receipt, CheckCircle2, AlertCircle, Pause } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  active: { icon: CheckCircle2, label: "Active", className: "bg-secondary/15 text-secondary" },
  overdue: { icon: AlertCircle, label: "Overdue", className: "bg-destructive/15 text-destructive" },
  paused: { icon: Pause, label: "Paused", className: "bg-muted text-foreground/70" },
};

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2 });

const AdminBilling = () => {
  const { role } = useAuth();
  const canEdit = role === "system";
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/billing").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const update = (patch) => setData({ ...data, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/billing", data);
      setData(res.data);
      toast.success("Billing updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const status = STATUS_META[data.status] || STATUS_META.active;
  const StatusIcon = status.icon;

  return (
    <div data-testid="admin-billing-page" className="max-w-3xl">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Receipt size={22} className="text-accent" /> Billing
          </h1>
          <p className="text-muted-foreground mt-1">
            {canEdit
              ? "Manage the township's hosting & maintenance billing."
              : "Your hosting & maintenance plan with FrostByte IT."}
          </p>
        </div>
        {canEdit && (
          <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="admin-billing-save-button">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        )}
      </div>

      <Card className="border-border mb-6">
        <CardContent className="p-7">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-7">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-secondary">Provider</div>
              <div className="font-heading text-xl font-semibold mt-0.5">{data.provider_name}</div>
              <a href={`https://${data.provider_contact}`} target="_blank" rel="noreferrer" className="text-sm text-secondary link-underline">
                {data.provider_contact}
              </a>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs uppercase tracking-wider ${status.className}`} data-testid="billing-status-badge">
              <StatusIcon size={12} /> {status.label}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="bg-muted/40 rounded-lg p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Initial setup fee</div>
              <div className="font-heading text-3xl font-semibold text-primary mt-1">{fmtMoney(data.setup_fee)}</div>
              <div className={`text-xs mt-1 ${data.setup_paid ? "text-secondary" : "text-muted-foreground"}`}>
                {data.setup_paid ? `Paid${data.setup_paid_date ? ` on ${data.setup_paid_date}` : ""}` : "One-time, due at launch"}
              </div>
            </div>
            <div className="bg-primary/5 rounded-lg p-5 border border-primary/10">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Recurring monthly</div>
              <div className="font-heading text-3xl font-semibold text-primary mt-1">{fmtMoney(data.monthly_fee)}<span className="text-sm font-normal text-muted-foreground"> /mo</span></div>
              <div className="text-xs text-muted-foreground mt-1">
                Hosting · maintenance · live chat · backups
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 text-sm">
            <div className="flex justify-between border-b border-border pb-3">
              <span className="text-muted-foreground">Last payment received</span>
              <span className="font-medium">{data.last_paid_date || "—"}</span>
            </div>
            <div className="flex justify-between border-b border-border pb-3">
              <span className="text-muted-foreground">Next payment due</span>
              <span className="font-medium">{data.next_due_date || "—"}</span>
            </div>
          </div>

          {data.notes && (
            <div className="mt-5 p-4 bg-muted/40 rounded-md text-sm whitespace-pre-wrap text-foreground/85">{data.notes}</div>
          )}

          {!canEdit && (
            <div className="mt-6 text-xs text-muted-foreground">
              Questions about billing? Reach out to {data.provider_name} at{" "}
              <a href={`https://${data.provider_contact}`} target="_blank" rel="noreferrer" className="link-underline">{data.provider_contact}</a>.
            </div>
          )}
        </CardContent>
      </Card>

      {canEdit && (
        <Card className="border-border" data-testid="admin-billing-edit-card">
          <CardContent className="p-7">
            <h2 className="font-heading text-lg font-semibold mb-4">Edit billing</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Setup fee ($)</Label>
                <Input type="number" step="0.01" value={data.setup_fee} onChange={(e) => update({ setup_fee: Number(e.target.value) })} className="mt-1.5" data-testid="billing-setup-fee" />
              </div>
              <div>
                <Label>Monthly fee ($)</Label>
                <Input type="number" step="0.01" value={data.monthly_fee} onChange={(e) => update({ monthly_fee: Number(e.target.value) })} className="mt-1.5" data-testid="billing-monthly-fee" />
              </div>
              <div className="flex items-center gap-2 mt-7">
                <input
                  id="setup-paid"
                  type="checkbox"
                  checked={!!data.setup_paid}
                  onChange={(e) => update({ setup_paid: e.target.checked })}
                  data-testid="billing-setup-paid"
                />
                <Label htmlFor="setup-paid">Setup fee paid</Label>
              </div>
              <div>
                <Label>Setup paid date</Label>
                <Input type="date" value={data.setup_paid_date || ""} onChange={(e) => update({ setup_paid_date: e.target.value })} className="mt-1.5" data-testid="billing-setup-paid-date" />
              </div>
              <div>
                <Label>Last payment date</Label>
                <Input type="date" value={data.last_paid_date || ""} onChange={(e) => update({ last_paid_date: e.target.value })} className="mt-1.5" data-testid="billing-last-paid" />
              </div>
              <div>
                <Label>Next due date</Label>
                <Input type="date" value={data.next_due_date || ""} onChange={(e) => update({ next_due_date: e.target.value })} className="mt-1.5" data-testid="billing-next-due" />
              </div>
              <div>
                <Label>Status</Label>
                <select
                  value={data.status}
                  onChange={(e) => update({ status: e.target.value })}
                  className="mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm"
                  data-testid="billing-status"
                >
                  <option value="active">active</option>
                  <option value="overdue">overdue</option>
                  <option value="paused">paused</option>
                </select>
              </div>
              <div>
                <Label>Provider name</Label>
                <Input value={data.provider_name || ""} onChange={(e) => update({ provider_name: e.target.value })} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>Provider contact</Label>
                <Input value={data.provider_contact || ""} onChange={(e) => update({ provider_contact: e.target.value })} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>Notes (visible to township admin)</Label>
                <Textarea rows={3} value={data.notes || ""} onChange={(e) => update({ notes: e.target.value })} className="mt-1.5" data-testid="billing-notes" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminBilling;
